import { isEqual } from 'date-fns';
import { useState } from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Vertical } from '#src/atoms/layout/flex/index.tsx';
import { BodyM, HeadlineL, LabelM } from '#src/atoms/typography/index.tsx';
import { IntlDate } from '#src/components/IntlDate/index.tsx';
import { EditableRow } from '#src/components/fiatWallet/CreateFiatWallet/EditableRow/index.tsx';
import { CreateFiatWalletSteps } from '#src/components/fiatWallet/CreateFiatWallet/type.ts';
import { useIsNotMajor } from '#src/components/fiatWallet/CreateFiatWallet/useIsNotMajor.ts';
import type { GraphQLResult } from '#src/components/form/Form/index.tsx';
import { GraphqlForm, TextField } from '#src/components/form/Form/index.tsx';
import { useCurrentUserContext } from '#src/contexts/currentUser/index.ts';
import { useCreateOrUpdateFiatWallet } from '#src/hooks/wallets/useCreateOrUpdateFiatWallet.ts';
import { glossary } from '#src/lib/glossary.ts';

const Content = styled(Vertical).attrs({ gap: 0 })`
  height: 100%;
`;

const Actions = styled(Vertical).attrs({ gap: 0 })`
  margin-top: var(--double-unit);
  flex-grow: 1;
  width: 100%;
  justify-content: flex-end;
`;
const StyledGraphqlForm = styled(GraphqlForm)`
  margin-bottom: 0;
  height: 100%;
`;

const Label = styled(BodyM)`
  color: var(--c-white);
`;

type Props = {
  setStep: (step: CreateFiatWalletSteps) => void;
};

export const ReviewInfoBeforeAddingId = ({ setStep }: Props) => {
  const { update } = useCreateOrUpdateFiatWallet();

  const { fiatWalletAccountable } = useCurrentUserContext();
  const { firstName, lastName, dob } = fiatWalletAccountable || {};

  // Dob should be not null at this step.
  const [year, month, day] = dob ? dob.split('-').map(Number) : [1990, 0, 1];

  const [newDob, setNewDob] = useState<Date>(
    new Date(Date.UTC(year, month - 1, day))
  );
  const [newFirstName, setNewFirstName] = useState<string>(firstName || '');
  const [newLastName, setNewLastName] = useState<string>(lastName || '');

  const updateAndSubmit = async (onResult: (result: GraphQLResult) => void) => {
    const newData = {
      firstName: newFirstName,
      lastName: newLastName,
      dob: newDob,
    };
    if (
      firstName !== newFirstName ||
      lastName !== newLastName ||
      (dob && !isEqual(newDob, new Date(dob)))
    ) {
      const updateResult = await update(newData);

      onResult(updateResult);
    }
    setStep(CreateFiatWalletSteps.CHOOSE_DOCUMENT);
  };

  const isNotMajor = useIsNotMajor(newDob);

  const disabled = isNotMajor || newFirstName === '' || newLastName === '';

  return (
    <>
      <HeadlineL as="h3">
        <FormattedMessage defaultMessage="Before adding your ID, review your personal info" />
      </HeadlineL>
      <BodyM>
        <FormattedMessage defaultMessage="Please review the info below for accuracy so we can verify your identity. Your ID should match the personal info in your Sorare account." />
      </BodyM>
      <StyledGraphqlForm
        onSubmit={(variables, onResult) => {
          updateAndSubmit(onResult);
        }}
        onSuccess={() => {
          setStep(CreateFiatWalletSteps.CHOOSE_DOCUMENT);
        }}
        render={(Error, SubmitButton) => (
          <Content>
            <Vertical gap={0}>
              <EditableRow
                value={newFirstName}
                label={
                  <Label>
                    <FormattedMessage {...glossary.firstName} />
                  </Label>
                }
              >
                <TextField
                  autoFocus
                  name="firstName"
                  defaultValue={newFirstName}
                  onChange={event => setNewFirstName(event?.target.value)}
                />
              </EditableRow>
              <EditableRow
                value={newLastName}
                label={
                  <Label>
                    <FormattedMessage {...glossary.lastName} />
                  </Label>
                }
              >
                <TextField
                  autoFocus
                  name="lastName"
                  onChange={event => {
                    setNewLastName(event?.target.value);
                  }}
                  defaultValue={newLastName}
                />
              </EditableRow>
              <EditableRow
                value={
                  <FormattedDate
                    value={newDob}
                    day="numeric"
                    month="long"
                    year="numeric"
                    timeZone="UTC"
                  />
                }
                label={
                  <Label>
                    <FormattedMessage {...glossary.dateOfBirth} />
                  </Label>
                }
              >
                <IntlDate
                  value={newDob}
                  onChange={date => {
                    setNewDob(date);
                  }}
                />
              </EditableRow>
              {!!isNotMajor && (
                <LabelM color="var(--c-red-400)">
                  <FormattedMessage {...glossary.notMajorError} />
                </LabelM>
              )}
            </Vertical>
            <Error />
            <Actions>
              <Error />
              <SubmitButton fullWidth size="medium" disabled={disabled}>
                <FormattedMessage {...glossary.next} />
              </SubmitButton>
            </Actions>
          </Content>
        )}
      />
    </>
  );
};
